class ProductZoomModal extends HTMLElement {
  constructor() {
    super();
    this.isZoomed = false;
    this.zoomLevel = 1;
    this.maxZoom = 3;
    this.minZoom = 1;
    this.currentX = 0;
    this.currentY = 0;
    this.isDragging = false;
    this.dragStart = { x: 0, y: 0 };
    this.lastTouchDistance = 0;
    this.isPinching = false;
    
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupImage();
  }

  setupEventListeners() {
    // Close button
    this.querySelector('.product-zoom-modal__close').addEventListener('click', () => this.hide());
    
    // Overlay click to close
    this.querySelector('.product-zoom-modal__overlay').addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.hide();
    });
    
    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.hide();
    });

    // Image interactions
    const imageContainer = this.querySelector('.product-zoom-modal__image-container');
    const image = this.querySelector('.product-zoom-modal__image');
    
    if (!imageContainer || !image) return;

    // Desktop: single click to zoom
    image.addEventListener('click', (e) => {
      if (this.isMobile()) return; // Skip on mobile
      e.preventDefault();
      this.toggleZoom();
    });

    // Mobile: double tap to zoom
    let lastTap = 0;
    image.addEventListener('touchend', (e) => {
      if (!this.isMobile()) return;
      
      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTap;
      
      if (tapLength < 500 && tapLength > 0) {
        e.preventDefault();
        this.toggleZoom();
      }
      lastTap = currentTime;
    });

    // Mouse wheel zoom
    imageContainer.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoom(delta);
    });

    // Touch events for pinch zoom and pan
    imageContainer.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
    imageContainer.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    imageContainer.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: false });

    // Mouse events for pan
    imageContainer.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    imageContainer.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    imageContainer.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    imageContainer.addEventListener('mouseleave', (e) => this.handleMouseUp(e));
  }

  setupImage() {
    const image = this.querySelector('.product-zoom-modal__image');
    if (!image) return;

    // Load high-res image
    const highResSrc = image.dataset.zoomSrc;
    if (highResSrc && highResSrc !== image.src) {
      const highResImg = new Image();
      highResImg.onload = () => {
        image.src = highResSrc;
        image.classList.remove('loading');
      };
      highResImg.onerror = () => {
        console.warn('Failed to load high-res image, using standard resolution');
        image.classList.remove('loading');
      };
      image.classList.add('loading');
      highResImg.src = highResSrc;
    }
  }

  isMobile() {
    return window.innerWidth <= 768 || 'ontouchstart' in window;
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  show(mediaId) {
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    
    // Show the correct image
    this.showActiveMedia(mediaId);
    
    // Reset zoom state
    this.resetZoom();
    
    // Focus trap
    this.trapFocus();
  }

  hide() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    this.resetZoom();
    this.removeTrapFocus();
  }

  showActiveMedia(mediaId) {
    // This will be called by the parent component to show the correct media
    const image = this.querySelector('.product-zoom-modal__image');
    if (image && mediaId) {
      // The image source should already be set by the handler
      // Just ensure it's visible and reset zoom state
      this.resetZoom();
    }
  }

  toggleZoom() {
    if (this.isZoomed) {
      this.resetZoom();
    } else {
      this.zoomToFit();
    }
  }

  zoom(delta) {
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));
    this.setZoom(newZoom);
  }

  setZoom(level) {
    this.zoomLevel = level;
    const image = this.querySelector('.product-zoom-modal__image');
    if (!image) return;

    image.style.transform = `scale(${this.zoomLevel}) translate(${this.currentX}px, ${this.currentY}px)`;
    this.isZoomed = this.zoomLevel > 1;
    
    // Update zoom indicator
    this.updateZoomIndicator();
  }

  zoomToFit() {
    this.setZoom(2); // Default zoom level
  }

  resetZoom() {
    this.zoomLevel = 1;
    this.currentX = 0;
    this.currentY = 0;
    this.isDragging = false;
    this.isZoomed = false;
    
    const image = this.querySelector('.product-zoom-modal__image');
    if (image) {
      image.style.transform = 'scale(1) translate(0px, 0px)';
    }
    
    this.updateZoomIndicator();
  }

  updateZoomIndicator() {
    const indicator = this.querySelector('.product-zoom-modal__zoom-indicator');
    if (!indicator) return;

    if (this.isZoomed) {
      indicator.style.opacity = '0';
    } else {
      indicator.style.opacity = '1';
    }
  }

  // Touch event handlers
  handleTouchStart(e) {
    if (e.touches.length === 1) {
      this.isDragging = true;
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      this.isPinching = true;
      this.lastTouchDistance = this.getTouchDistance(e.touches);
    }
  }

  handleTouchMove(e) {
    if (!this.isZoomed) return;
    
    e.preventDefault();
    
    if (this.isPinching && e.touches.length === 2) {
      const currentDistance = this.getTouchDistance(e.touches);
      const scale = currentDistance / this.lastTouchDistance;
      this.zoom((scale - 1) * 0.5);
      this.lastTouchDistance = currentDistance;
    } else if (this.isDragging && e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - this.dragStart.x;
      const deltaY = e.touches[0].clientY - this.dragStart.y;
      this.pan(deltaX, deltaY);
    }
  }

  handleTouchEnd(e) {
    this.isDragging = false;
    this.isPinching = false;
  }

  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Mouse event handlers
  handleMouseDown(e) {
    if (!this.isZoomed) return;
    this.isDragging = true;
    this.dragStart = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging || !this.isZoomed) return;
    e.preventDefault();
    
    const deltaX = e.clientX - this.dragStart.x;
    const deltaY = e.clientY - this.dragStart.y;
    this.pan(deltaX, deltaY);
  }

  handleMouseUp(e) {
    this.isDragging = false;
  }

  pan(deltaX, deltaY) {
    const maxPan = 100; // Adjust based on your needs
    this.currentX = Math.max(-maxPan, Math.min(maxPan, this.currentX + deltaX));
    this.currentY = Math.max(-maxPan, Math.min(maxPan, this.currentY + deltaY));
    
    this.setZoom(this.zoomLevel);
    this.dragStart = { x: this.dragStart.x + deltaX, y: this.dragStart.y + deltaY };
  }

  // Focus management
  trapFocus() {
    const focusableElements = this.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  removeTrapFocus() {
    // Return focus to the element that opened the modal
    if (this.openedBy) {
      this.openedBy.focus();
    }
  }
}

// Register the custom element
if (!customElements.get('product-zoom-modal')) {
  customElements.define('product-zoom-modal', ProductZoomModal);
}